// The app's two routes, under the base path (VITE_BASE_PATH, e.g. "/" or "/v1-meet/"):
//
//   <base>             the lobby for a new meeting
//   <base>r/<code>     the lobby for that meeting, then the room (invite links point here)
//
// "?room=<code>" is accepted too (the P2 iframe snippet forwards it). Pure functions; the
// History API calls are in App.tsx.

/** 13 characters of lower-case Crockford base32 (65 random bits), as token/lib/rooms.js
 *  (scripts/check-protocol.mjs keeps them equal). */
export const ROOM_CODE_PATTERN = /^[0-9a-hjkmnp-tv-z]{13}$/;

export interface Route {
  /** The meeting to join; null = start a new one. */
  readonly code: string | null;
  /** True when the URL named a meeting code that is not valid. */
  readonly invalidCode: boolean;
}

function withTrailingSlash(base: string): string {
  return base.endsWith('/') ? base : `${base}/`;
}

function normalizeCode(value: string): string {
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    // A broken escape is simply not a valid code.
  }
  return decoded.trim().toLowerCase();
}

export function parseRoute(pathname: string, search: string, base: string): Route {
  const root = withTrailingSlash(base);
  // The site may redirect "/v1-meet/" to "/v1-meet" (Next.js trailingSlash: false): both are the root.
  let rest = pathname;
  if (pathname === root.slice(0, -1)) rest = '';
  else if (pathname.startsWith(root)) rest = pathname.slice(root.length);

  const fromPath = /^r\/([^/]+)\/?$/.exec(rest)?.[1];
  const fromQuery = new URLSearchParams(search).get('room');
  const candidate = fromPath ?? fromQuery;
  if (candidate === undefined || candidate === null) return { code: null, invalidCode: false };

  const code = normalizeCode(candidate);
  return ROOM_CODE_PATTERN.test(code) ? { code, invalidCode: false } : { code: null, invalidCode: true };
}

export function roomPath(code: string, base: string): string {
  return `${withTrailingSlash(base)}r/${code}`;
}

export function homePath(base: string): string {
  return withTrailingSlash(base);
}

/** The link people share: the page's own origin, so under the rewrite it is versiyonbir.com. */
export function inviteUrl(code: string, base: string, origin: string): string {
  return new URL(roomPath(code, base), origin).toString();
}
